import { ChangeEvent, useEffect, useState } from "react"
import { GetServerSidePropsContext } from "next";
import { useSettings } from '@/data/Settings';
import { useExtraRequestParameters } from "@/data/Content/_ExtraRequestParameters";
import { ID } from "@/data/types/Basic";
import { displayStoreWithStock, LatLng, StoreDetails } from "@/data/types/Store";
import { storeLocatorByUniqueIdsFetcher, dataMap as STLOCDataMap } from "@/data/Content/StoreLocator";
import { findStoreAttributeValueByName } from "@/data/utils/StoreStock";
import { filterStoresWithStockAvailability } from "@/data/utils/storeUtil";
import { getStoreFeature } from "@/utils/storeDetails";
import { StoreStockSapInventory } from "integration/generated/customTransactions/data-contracts";
import { transactionsStoreLocator } from "integration/generated/transactions";
import { RequestParams } from "integration/generated/transactions/http-client";
import { StorelocatorStorelocator } from "integration/generated/transactions/data-contracts";
import { customTransactionsSapInventory } from "integration/generated/customTransactions";
import { error as logError } from '@/data/utils/loggerUtil';
import useSWR from "swr";

const DATA_KEY_CLICK_AND_COLLECT = 'DATA_KEY_CLICK_AND_COLLECT';

export const storesFetcher =
    (pub: boolean, context?: GetServerSidePropsContext) =>
    async (
        storeId: string,
        latitude: string,
        longitude: string,
        storeUniqueIds:string[],
        query: {
            [key: string]: string | boolean | ID[] | number;
        },
        params: RequestParams,
    ) => {
        try {
            return  !storeUniqueIds?.length ?
                await transactionsStoreLocator(pub).storeLocatorFindStores(
                    storeId,
                    latitude,
                    longitude,
                    query,
                    params
                )
                : await storeLocatorByUniqueIdsFetcher(true, context)(
                    storeId, 
                    {
                        physicalStoreId:storeUniqueIds
                    }, 
                    params
                );
        } catch (error) {
            logError(context?.req, 'Click & Collect: stores fetcher: error: %o', error);
        }
    };

export const stockFetcher =
    (pub: boolean, context?: GetServerSidePropsContext) =>
    async (
        storeId: string,
        storeNumber:string,
        partNumber:string,
        params: RequestParams,
    ) => {
        try {
            return await customTransactionsSapInventory(pub).sapInventoryAvailabilityGetStoreInventoryByStoreNumbersAndPartNumberDetail(
                storeId,
                {
                    storeNumber,
                    partNumber,
                    hasLimit:false,
                    isOrder:true
                },
                params
            );
        } catch (error) {
            logError(context?.req, 'Click & Collect: stock fetcher: error: %o', error);
        }
    };

export const fetcher =
    (pub: boolean, context?: GetServerSidePropsContext) =>
    async (
        storeId: string,
        latitude: string,
        longitude: string,
        partNumber:string,
        storeUniqueIds:string[],
        storepickAllowedAttribute:string,
        storepickAllowedAttributeValue:string,
        query: {
            [key: string]: string | boolean | ID[] | number;
        },
        params: RequestParams,
    ) => {
        try {
            const storesData = await storesFetcher(pub, context)(
                storeId,
                latitude,
                longitude,
                storeUniqueIds,
                query,
                params,
            );

            const stores  = STLOCDataMap(storesData as StorelocatorStorelocator );

            const storeNumber = (storeUniqueIds?.length ? stores : stores.filter(
                s => findStoreAttributeValueByName(s.attributes, storepickAllowedAttribute) === storepickAllowedAttributeValue
              )).map(s => s.physicalStoreName || "").filter(Boolean).join(",");

            const storeStockData = await stockFetcher(pub, context)(
                storeId,
                storeNumber,
                partNumber,
                params
            );

            const selectedStore = storeUniqueIds?.length ? stores?.at(0) : undefined;

            return {
                stores: selectedStore ? [] : stores,
                storeStockData,
                selectedStore
            };
            
        } catch (error) {
            logError(context?.req, 'Click & Collect: fetcher: error: %o', error);
        }
    }

export const useClickAndCollect = (partNumbers:string[]) => {
    const {settings} = useSettings();

    const [latLng, setLatLng] = useState<LatLng>({} as LatLng);
    const [storeUniqueIds, setStoreUniqueIds] = useState<string[]>([]);
    
    const [stores, setStores] = useState<StoreDetails[]>([]);
    const [storeStock, setStoreStock] = useState<StoreStockSapInventory>();
    const [selectedStoreCC, setSelectedStoreCC] = useState<displayStoreWithStock | undefined>();
    const [displayStores, setDisplayStores] = useState<displayStoreWithStock[]>([]);
    const [showSearchByLocation, setShowSearchByLocation] = useState(false);
    const [showSearchNearStores, setShowSearchNearStores] = useState(false);
    const [showNostoresWithStockMsg, setShowNoStoresWithStockMsg] = useState(false);
    const [hasLocationAccess, setLocationAccess] = useState(false);
    const [isSapStockStatusAvailable, setSapStockStatusAvailable] = useState(true);

    const autoCompleteOptions = {
        radius:settings.userData.StoreLocator_Radius,
        radiusUOM:settings.userData.StoreLocator_RadiusUOM,
        maxItems:settings.userData.StoreLocatorStoreStockPDP_MaxItems??'25',
        siteLevelStoreSearch: false,
    }
    const allowStorePickForOOSItems = settings.userData.AllowStorePickForOOSItems;
    const storepickAllowedAttribute = settings.userData.StorepickAllowedAttribute;
    const storepickAllowedAttributeValue = settings.userData.StorepickAllowedAttributeValue;
    const storeEndTimeCutOff = settings.userData.ClickAndCollectStoreEndTimeCutOff;
    const clickAndCollectShowStoresCount = parseInt(settings.userData.ClickAndCollectShowStoresCount??'5');

    const params = useExtraRequestParameters();

    const { data, isLoading } = useSWR(
        (latLng?.lat || (storeUniqueIds.length && !storeUniqueIds?.includes(selectedStoreCC?.store?.id??''))) && partNumbers.length
            ? [
                    {
                        storeId: settings?.storeId ?? '',
                        latitude: String(latLng.lat),
                        longitude: String(latLng.lng),
                        query: {...autoCompleteOptions},
                        partNumbers,
                        storeUniqueIds
                    },
                    DATA_KEY_CLICK_AND_COLLECT,
              ]
            : null,
        async ([props]) =>
            fetcher(true)(
                props.storeId, 
                props.latitude, 
                props.longitude, 
                props.partNumbers?.join(","),
                props.storeUniqueIds,
                storepickAllowedAttribute,
                storepickAllowedAttributeValue,
                props.query,
                params
            ),
    );

    useEffect(()=>{
        const storeStock = data?.storeStockData;
        const inventory = storeStock?.inventory ?? [];
        const currentDay = parseInt(storeStock?.day ?? '0');

        if(data?.stores?.length && inventory?.length){

            setStores(data?.stores);
            setStoreStock(storeStock);

            const inventoryByStore = new Map(inventory?.map(iv => [iv.storeNumber, iv.available]) ?? []);
            const tempDisplayStores = filterStoresWithStockAvailability(
                data?.stores ?? [], 
                inventory, 
                clickAndCollectShowStoresCount, 
                allowStorePickForOOSItems
            ).map((store) => ({
                store,
                stock: inventoryByStore.get(store.physicalStoreName),
                feature: getStoreFeature(store, currentDay, storeEndTimeCutOff),
            }));

            setDisplayStores(tempDisplayStores);
            
        } else if (data?.selectedStore) {
            const selectedStore = data.selectedStore;
        
            const tempDisplayStore: displayStoreWithStock = {
                store: selectedStore,
                stock: inventory?.find(iv => iv.storeNumber === selectedStore?.physicalStoreName)?.available,
                feature: getStoreFeature(selectedStore, currentDay, storeEndTimeCutOff)
            };
        
            setSelectedStoreCC(tempDisplayStore);
        }

        if(data && storeStock && (storeStock.status===500 || storeStock.status===503)){
            setSapStockStatusAvailable(false);
        }

        if(!isLoading){
            if(latLng?.lat) setLatLng({} as LatLng);
            if(storeUniqueIds?.length) setStoreUniqueIds([]);
        }

    },[data, isLoading]);

    useEffect(() => {
        let timeout:NodeJS.Timeout;
        if ((showSearchNearStores || (showSearchByLocation && hasLocationAccess)) && displayStores?.length === 0 && !isLoading) {
            // adding small delay to wait isSapStoreStockLoading to start and avoid flicker
            timeout = setTimeout(()=>setShowNoStoresWithStockMsg(true),50);
        } else {
            setShowNoStoresWithStockMsg(false);
        }
        return ()=>clearTimeout(timeout);
    },[ showSearchNearStores,  showSearchByLocation, displayStores, isLoading ]);


    const handleShowNearStoresCheckChange = (event: ChangeEvent<HTMLInputElement>) => {
        let tempShowCCStoreCount = clickAndCollectShowStoresCount;
        let tempStores = [...stores];
        const checked = event?.target?.checked;
        setShowSearchNearStores(checked)
        if (checked) {
            tempShowCCStoreCount = tempStores.length
        }
        if (stores?.length > 0 && storeStock?.inventory) {
            const inventory = storeStock?.inventory ?? [];
            const currentDay = parseInt(storeStock?.day ?? '0');
            let tempStores = filterStoresWithStockAvailability(
                [...stores], inventory, 5, allowStorePickForOOSItems, tempShowCCStoreCount
            )
            let tempDisplayStores = tempStores?.map((store) => ({
                store,
                stock: inventory?.find(iv => iv.storeNumber === store.physicalStoreName)?.available,
                feature: getStoreFeature(store, currentDay, storeEndTimeCutOff)
            }))
            setDisplayStores(tempDisplayStores);
        }
    }

    /**
     * Resetting searched store data after user selects a store in checkout CC popup
     * If not reset, previous searched data will be shown on the popup on next open
     */
    useEffect(()=>{
        if(storeUniqueIds?.includes(selectedStoreCC?.store?.id??'')){
            setStores([])
            setDisplayStores([]);
        }
    },[storeUniqueIds]);

    return {
        latLng,
        setLatLng,
        stores,
        setStores,
        setStoreUniqueIds,
        selectedStoreCC,
        setSelectedStoreCC,
        displayStores,
        setDisplayStores,
        handleShowNearStoresCheckChange,
        showSearchByLocation,
        setShowSearchByLocation,
        showSearchNearStores,
        setShowSearchNearStores,
        showNostoresWithStockMsg,
        isLoading,
        setLocationAccess,
        isSapStockStatusAvailable
    }
};